// Recomputes and persists the per-student Fehlerindex outcome (Submission.grammarGradingSummary):
// aggregate FI over every grammar-eligible task, WHENEVER the alwaysUseFi switch (exam override, else
// teacher default) is on — independent of whether a task also has a configured grammar rule. Rules
// grading and FI are independent, stackable layers: a rule-covered task still gets graded via its rule
// (see gradingAgent.ts), and its errors ALSO count toward this exam-wide FI deduction when the switch
// is on. Triggered after grading runs, on FI-settings changes, and after manual grammar-error edits.
// If the switch is off (or the exam has no grammar-eligible tasks at all), the summary is cleared for
// every submission.

import { logger } from '@adornis/base/logging.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { parseGradingScale } from '../../shared/grading-scales.js';
import { applyGradeStepDeduction, fiDeductionSteps, parseGrammarFiSettings } from '../../shared/grammar-grading.js';
import { resolveAlwaysUseFi, taskGrammarMode } from '../../shared/grammar-rules-resolution.js';
import { Exam } from '../exam.js';
import { StepState } from '../extraction-progress.js';
import { Submission } from '../submission.js';
import { Teacher } from '../teacher.js';
import { createTeacherSummary } from './generate-grading-reports.js';

/** Keyed by the same "|"-joined qualified path LanguageUseScheme.dependsOn now stores (see
 * build-exam-tasks.ts's collectAllTaskNumbers) — a bare local taskNumber like "a" is ambiguous
 * whenever it's reused as a subtask label under more than one parent. */
function collectTaskMap(
  tasks: any[] | null | undefined,
  parentPath: string[] = [],
  map = new Map<string, any>(),
): Map<string, any> {
  for (const task of Array.isArray(tasks) ? tasks : []) {
    const tn = task?.taskNumber ? String(task.taskNumber) : '';
    const path = tn ? [...parentPath, tn] : parentPath;
    if (tn) map.set(path.join('|'), task);
    collectTaskMap(task?.subTasks, path, map);
  }
  return map;
}

export async function persistGrammarFiOutcomes(examID: string): Promise<void> {
  try {
    const examCollection = await getCollection(Exam);
    const examDoc: any = await examCollection.findOne({ _id: examID });
    if (!examDoc) return;

    const submissionCollection = await getCollection(Submission);
    const submissions: any[] = await submissionCollection.find({ examId: examID }).toArray();
    if (submissions.length === 0) return;

    const raw = await getRawCollection(Submission._collectionName);
    const settings = parseGrammarFiSettings(examDoc.grammarFiSettingsJson);

    let teacherAlwaysUseFi: boolean | undefined;
    if (examDoc.userID) {
      const teacherCollection = await getCollection(Teacher);
      const teacher = await teacherCollection.findOne({ userID: examDoc.userID });
      teacherAlwaysUseFi = (teacher as any)?.grammarPreferences?.alwaysUseFi;
    }
    const forceFi = resolveAlwaysUseFi(settings.alwaysUseFi, teacherAlwaysUseFi);

    const taskMap = collectTaskMap(examDoc.tasks);
    const languageUseSchemeRules = Array.isArray(examDoc.languageUseSchemeRules) ? examDoc.languageUseSchemeRules : [];
    // FI applies to every grammar-eligible task (rule-covered or not) once the switch is on — the
    // switch, not per-task rule coverage, decides whether this aggregate deduction applies at all.
    const isFiTask = (taskNumber: string): boolean => {
      if (!forceFi) return false;
      const examTask = taskMap.get(taskNumber);
      if (!examTask) return false;
      return taskGrammarMode(examTask, taskNumber, languageUseSchemeRules) !== 'none';
    };

    if (!Array.from(taskMap.keys()).some(isFiTask)) {
      await raw.updateMany({ examId: examID } as any, { $set: { grammarGradingSummary: null } } as any);
      return;
    }

    const scale = parseGradingScale(examDoc.gradingScaleJson);
    // baseGrade is the pure points-derived grade (pre-deduction), so reusing the summary here is
    // not circular even though createTeacherSummary also applies previously persisted deductions.
    const summary = createTeacherSummary(examDoc, submissions);
    const baseGradeById = new Map(summary.studentSummaries.map(s => [s.id, s.baseGrade]));

    const computedAt = new Date().toISOString();
    for (const submission of submissions) {
      const submissionId = String(submission._id);

      let errorCount = 0;
      let wordCount = 0;
      let hasCompletedEvaluation = false;
      // Grammar evaluations can live on a nested subtask (e.g. a split essay/poetry task combined
      // into one main task with subtasks), so this must walk the whole subTasksSubmission tree —
      // tracking the same "|"-joined ancestor path isFiTask/taskMap expect, since a bare taskNumber
      // is ambiguous whenever it's reused as a subtask label under more than one parent.
      const collectFromNode = (taskSubmission: any, parentPath: string[]) => {
        const tn = taskSubmission?.taskNumber ? String(taskSubmission.taskNumber) : '';
        const path = tn ? [...parentPath, tn] : parentPath;
        const evaluation = taskSubmission?.grammarEvaluation;
        if (evaluation?.status === StepState.COMPLETED && isFiTask(path.join('|'))) {
          hasCompletedEvaluation = true;
          errorCount += Array.isArray(evaluation.errors) ? evaluation.errors.length : Number(evaluation.errorCount) || 0;
          wordCount += Number(evaluation.wordCount) || 0;
        }
        for (const child of Array.isArray(taskSubmission?.subTasksSubmission) ? taskSubmission.subTasksSubmission : []) {
          collectFromNode(child, path);
        }
      };
      for (const taskSubmission of Array.isArray(submission.taskSubmissions) ? submission.taskSubmissions : []) {
        collectFromNode(taskSubmission, []);
      }

      if (!hasCompletedEvaluation) {
        await raw.updateOne({ _id: submissionId } as any, { $set: { grammarGradingSummary: null } } as any);
        continue;
      }

      const fehlerindex = wordCount > 0 ? (errorCount / wordCount) * 100 : 0;
      const deductionSteps = fiDeductionSteps(fehlerindex, settings);
      const baseGrade = baseGradeById.get(submissionId) ?? null;
      const adjustedGrade = baseGrade != null ? applyGradeStepDeduction(baseGrade, deductionSteps, scale) : null;

      await raw.updateOne(
        { _id: submissionId } as any,
        {
          $set: {
            grammarGradingSummary: {
              _class: 'GrammarGradingSummary',
              errorCount,
              wordCount,
              fehlerindex,
              // Reaching this write already means FI is the active grading mode for this submission
              // (see the isFiTask early-return above) — no separate "deduction enabled" toggle exists.
              fiApplied: true,
              deductionSteps,
              ...(baseGrade != null ? { baseGrade } : {}),
              ...(adjustedGrade != null ? { adjustedGrade } : {}),
              computedAt,
            },
          },
        } as any,
      );
    }

    logger.info(
      { examID, submissionCount: submissions.length, fiEnabled: settings.fiEnabled, context: 'GRAMMAR_FI' },
      'Persisted grammar FI outcomes',
    );
  } catch (err) {
    logger.error({ err, examID, context: 'GRAMMAR_FI' }, 'Failed to persist grammar FI outcomes');
  }
}
